import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useBar } from "../../../context/BarContext";
import { useUserRole } from "../../../context/UserRoleContext";
import useBarArtists from "../hooks/useBarArtists";
import { useMessaging } from "../../../context/MessagingContext";
import tempArtistImage from "../../../assets/images/artist.gif";
import ConfirmModal from "./ConfirmModal";
import { validateTimeRange } from "../../../utils/timeValidation";
import {
  getEffectiveSlotsForDate,
  parseTimeSlots,
  timeToMinutes,
} from "../../../../lib/barTimeSlots";
import { formatDateForInput } from "../../../utils/calendarDate";
import {
  formatCivilDateLong,
  normalizeBarTimeZone,
  civilDateTodayInZone,
  parseCivilDateOnly,
} from "../../../utils/civilDateTime";

const formatTimeDisplay = (time) => {
  if (!time) return "";
  const [hoursStr, minutesStr] = time.split(":");
  const hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr, 10) || 0;

  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return time;
  }

  const ampm = hours >= 12 ? "PM" : "AM";
  const normalizedHour = hours % 12 === 0 ? 12 : hours % 12;
  const paddedMinutes = minutes.toString().padStart(2, "0");

  return `${normalizedHour}:${paddedMinutes} ${ampm}`;
};

const formatCurrency = (amount) => {
  const value = Number(amount) || 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
};

const doesShowMatchSlotExactly = (show, slot) => {
  const a = timeToMinutes(show.start_time);
  const b = timeToMinutes(show.end_time);
  const c = timeToMinutes(slot.start_time);
  const d = timeToMinutes(slot.end_time);
  return (
    a != null && b != null && c != null && d != null && a === c && b === d
  );
};

const buildFormState = (source) => ({
  artistId: source?.artist_id ? String(source.artist_id) : "",
  date: source?.date ? formatDateForInput(source.date) : "",
  startTime: source?.start_time ? source.start_time.slice(0, 5) : "",
  endTime: source?.end_time ? source.end_time.slice(0, 5) : "",
  timeSlotId: "",
  basePay:
    source?.base_pay !== undefined && source?.base_pay !== null
      ? String(source.base_pay)
      : "",
  sales:
    source?.sales !== undefined && source?.sales !== null
      ? String(source.sales)
      : "",
});

const ensureSeconds = (timeValue) => {
  if (!timeValue) return "";
  return timeValue.length === 5 ? `${timeValue}:00` : timeValue;
};

const ShowCardModal = ({ isOpen, onClose, showId, fallbackShow }) => {
  const navigate = useNavigate();
  const { userData } = useUserRole();
  const { selectedBar, refreshBarShows } = useBar();
  const { artists, loading: artistsLoading } = useBarArtists(
    selectedBar?.bar_id
  );
  const { getOrCreateConversation } = useMessaging();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showDetails, setShowDetails] = useState(null);
  const [formState, setFormState] = useState({
    artistId: "",
    date: "",
    startTime: "",
    endTime: "",
    timeSlotId: "",
    basePay: "",
    sales: "",
  });
  const [useCustomTimes, setUseCustomTimes] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [timeError, setTimeError] = useState("");

  const venueName = selectedBar?.name || "Venue";
  const [isMessaging, setIsMessaging] = useState(false);

  const timeSlotOptions = useMemo(() => {
    if (!selectedBar?.time_slots || !formState.date) return [];
    return getEffectiveSlotsForDate(selectedBar.time_slots, formState.date);
  }, [selectedBar?.time_slots, formState.date]);

  const namedSlotsAvailable = useMemo(() => {
    const p = parseTimeSlots(selectedBar?.time_slots);
    return p.enabled && timeSlotOptions.length > 0;
  }, [selectedBar?.time_slots, timeSlotOptions.length]);

  const placeholderShow = useMemo(() => {
    if (!fallbackShow) return null;
    return {
      artist_name: fallbackShow.artist_name,
      stage_name: fallbackShow.stage_name,
      artist_id: fallbackShow.artist_id,
      bar_id: fallbackShow.bar_id,
      show_id: fallbackShow.show_id,
      date: fallbackShow.date,
      start_time: fallbackShow.start_time,
      end_time: fallbackShow.end_time,
      base_pay: fallbackShow.base_pay,
      sales: fallbackShow.sales,
    };
  }, [fallbackShow]);

  useEffect(() => {
    if (!isOpen || !showId) {
      setShowDetails(null);
      setError("");
      setLoading(false);
      setSaveMessage("");
      setIsEditing(false);
      return;
    }

    let isCancelled = false;

    const fetchShowDetails = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await fetch(`/api/earnings/show/${showId}`, {
          credentials: "include",
        });
        const data = await response.json();

        if (isCancelled) return;

        if (!data.success || !data.earnings) {
          throw new Error(data.error || "Unable to load show details");
        }

        setShowDetails(data.earnings);
        setFormState(buildFormState(data.earnings));
      } catch (fetchError) {
        console.error("Failed to fetch show earnings:", fetchError);
        if (!isCancelled) {
          setError(fetchError.message || "Failed to load show details.");
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchShowDetails();

    return () => {
      isCancelled = true;
    };
  }, [isOpen, showId]);

  useEffect(() => {
    if (!isOpen || isEditing) return;
    const source = showDetails || placeholderShow;
    if (source) {
      setFormState(buildFormState(source));
    }
  }, [isOpen, isEditing, showDetails, placeholderShow]);

  useEffect(() => {
    if (!isOpen) {
      setUseCustomTimes(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !isEditing) return;
    const slots = getEffectiveSlotsForDate(
      selectedBar?.time_slots,
      formState.date
    );
    const p = parseTimeSlots(selectedBar?.time_slots);
    if (!p.enabled || slots.length === 0) {
      setUseCustomTimes(true);
    }
  }, [isOpen, isEditing, formState.date, selectedBar?.time_slots]);

  useEffect(() => {
    if (!isOpen || !isEditing || useCustomTimes) return;
    const slots = getEffectiveSlotsForDate(
      selectedBar?.time_slots,
      formState.date
    );
    const ok = slots.some((s) => String(s.id) === String(formState.timeSlotId));
    if (formState.timeSlotId && !ok) {
      setFormState((prev) => ({ ...prev, timeSlotId: "" }));
    }
  }, [
    isOpen,
    isEditing,
    formState.date,
    formState.timeSlotId,
    selectedBar?.time_slots,
    useCustomTimes,
  ]);

  if (!isOpen) {
    return null;
  }

  const detailSource = showDetails || placeholderShow;

  const venueTz = normalizeBarTimeZone(selectedBar?.time_zone);
  const todayYmd = civilDateTodayInZone(venueTz);
  const showYmd = parseCivilDateOnly(formatDateForInput(detailSource?.date));
  const isPastShow = Boolean(showYmd && showYmd < todayYmd);

  const artistName =
    detailSource?.stage_name || detailSource?.artist_name || "Artist";
  const artistUserName =
    detailSource?.artist_user_name ||
    detailSource?.artist_name ||
    detailSource?.stage_name ||
    artistName;
  const artistUserId = detailSource?.artist_user_id;

  const dateDisplay = formatCivilDateLong(detailSource?.date, venueTz);
  const startTimeDisplay = formatTimeDisplay(detailSource?.start_time);
  const endTimeDisplay = formatTimeDisplay(detailSource?.end_time);
  const basePayDisplay = formatCurrency(
    detailSource?.base_pay ?? formState.basePay ?? 0
  );
  const salesDisplay = formatCurrency(
    detailSource?.sales ?? formState.sales ?? 0
  );

  const handleInputChange = (field) => (event) => {
    const { value } = event.target;
    setFormState((prev) => {
      const newState = {
        ...prev,
        [field]: value,
        ...(field === "startTime" || field === "endTime"
          ? { timeSlotId: "" }
          : {}),
      };

      // Validate times if both are present
      if (
        (field === "startTime" || field === "endTime") &&
        (newState.startTime || newState.endTime)
      ) {
        if (newState.startTime && newState.endTime) {
          const validation = validateTimeRange(
            newState.startTime,
            newState.endTime
          );
          setTimeError(validation.isValid ? "" : validation.error);
        } else {
          setTimeError("");
        }
      }

      return newState;
    });
    setSaveMessage("");
  };

  const handleEditTimeSlotChange = (event) => {
    const { value } = event.target;
    const slot = timeSlotOptions.find((s) => String(s.id) === String(value));
    setFormState((prev) => ({
      ...prev,
      timeSlotId: value,
      startTime: slot ? String(slot.start_time).slice(0, 5) : "",
      endTime: slot ? String(slot.end_time).slice(0, 5) : "",
    }));
    setTimeError("");
    setSaveMessage("");
  };

  const handleToggleEditCustomTimes = () => {
    setFormState((s) => {
      if (!useCustomTimes) {
        return { ...s, timeSlotId: "" };
      }
      return { ...s, startTime: "", endTime: "" };
    });
    setUseCustomTimes((prev) => !prev);
    setTimeError("");
    setSaveMessage("");
  };

  const handleNumberInput = (field) => (event) => {
    const { value } = event.target;
    const sanitized = value.replace(/\D/g, "");
    setFormState((prev) => ({
      ...prev,
      [field]: sanitized,
    }));
    setSaveMessage("");
  };

  const handleModalClose = () => {
    if (isSaving) return;
    setIsEditing(false);
    setUseCustomTimes(false);
    setSaveMessage("");
    onClose();
  };

  const handleStartEdit = () => {
    const source = showDetails || placeholderShow;
    if (!source) return;
    const ymd = formatDateForInput(source.date);
    const slots = getEffectiveSlotsForDate(selectedBar?.time_slots, ymd);
    const p = parseTimeSlots(selectedBar?.time_slots);
    const matched = slots.find((slot) =>
      doesShowMatchSlotExactly(
        {
          start_time: source.start_time,
          end_time: source.end_time,
        },
        slot
      )
    );
    const namedOk = p.enabled && slots.length > 0;
    setUseCustomTimes(!(namedOk && matched));
    setFormState({
      ...buildFormState(source),
      timeSlotId: matched ? String(matched.id) : "",
    });
    setIsEditing(true);
    setSaveMessage("");
    setTimeError("");
  };

  const handleCancelEdit = () => {
    const source = showDetails || placeholderShow;
    if (source) {
      setFormState(buildFormState(source));
    }
    setUseCustomTimes(false);
    setIsEditing(false);
    setSaveMessage("");
    setTimeError("");
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!showId) return;

    if (timeError && useCustomTimes) {
      setError(timeError);
      return;
    }

    setIsSaving(true);
    setSaveMessage("");
    setError("");

    try {
      const updates = {};

      if (formState.artistId) {
        updates.artist_id = Number(formState.artistId);
      }
      if (formState.date) {
        updates.date = formState.date;
      }

      let resolvedStart = "";
      let resolvedEnd = "";
      if (namedSlotsAvailable && !useCustomTimes) {
        const slot = timeSlotOptions.find(
          (s) => String(s.id) === String(formState.timeSlotId)
        );
        if (!slot) {
          setError(
            "Select a venue time slot, or use custom start and end times."
          );
          return;
        }
        resolvedStart = ensureSeconds(String(slot.start_time).slice(0, 5));
        resolvedEnd = ensureSeconds(String(slot.end_time).slice(0, 5));
      } else if (formState.startTime && formState.endTime) {
        const validation = validateTimeRange(
          formState.startTime,
          formState.endTime
        );
        if (!validation.isValid) {
          setError(validation.error);
          return;
        }
        resolvedStart = ensureSeconds(formState.startTime);
        resolvedEnd = ensureSeconds(formState.endTime);
      } else {
        setError("Enter both a start time and an end time.");
        return;
      }

      if (resolvedStart) {
        updates.start_time = resolvedStart;
      }
      if (resolvedEnd) {
        updates.end_time = resolvedEnd;
      }

      if (Object.keys(updates).length > 0) {
        const showUpdateResponse = await fetch(`/api/shows/${showId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(updates),
        });
        const showUpdateData = await showUpdateResponse.json();
        if (!showUpdateData.success) {
          throw new Error(showUpdateData.error || "Failed to update show");
        }
        setShowDetails((prev) => ({
          ...(prev || {}),
          ...showUpdateData.show,
        }));
      }

      const earningsPayload = {
        show_id: Number(showId),
        base_pay: Number.parseInt(formState.basePay || "0", 10) || 0,
        sales: Number.parseInt(formState.sales || "0", 10) || 0,
      };

      const earningsResponse = await fetch("/api/earnings", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(earningsPayload),
      });
      const earningsData = await earningsResponse.json();

      if (!earningsData.success) {
        throw new Error(earningsData.error || "Failed to update earnings");
      }

      setShowDetails((prev) => ({
        ...(prev || {}),
        ...earningsData.earnings,
        base_pay: earningsPayload.base_pay,
        sales: earningsPayload.sales,
      }));

      setIsEditing(false);
      setSaveMessage("Changes saved successfully.");
      await refreshBarShows();
    } catch (saveError) {
      console.error("Failed to save show details:", saveError);
      setError(saveError.message || "Failed to save changes.");
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDelete = () => {
    if (!showId || isDeleting) return;
    setIsDeleteConfirmationOpen(true);
  };

  const handleDelete = async () => {
    if (!showId || isDeleting) return;

    try {
      setIsDeleting(true);
      setError("");
      setSaveMessage("");

      const response = await fetch(`/api/shows/${showId}`, {
        method: "DELETE",
        credentials: "include",
      });
      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || "Failed to delete show.");
      }

      await refreshBarShows();
      onClose();
    } catch (deleteError) {
      console.error("Failed to delete show:", deleteError);
      setError(deleteError.message || "Failed to delete show.");
    } finally {
      setIsDeleting(false);
      setIsDeleteConfirmationOpen(false);
    }
  };

  const handleMessageArtist = async () => {
    if (!artistUserId || isMessaging) return;
    try {
      setIsMessaging(true);
      const conversation = await getOrCreateConversation(artistUserId);
      if (conversation?.conversation_id) {
        const basePath =
          userData?.role === "bar_admin" ? "/bar-admin" : "/bar-owner";
        navigate(
          `${basePath}/messages?conversation=${conversation.conversation_id}`
        );
      }
    } catch (messageError) {
      console.error("Failed to start conversation:", messageError);
      setError("Unable to start a conversation right now. Please try again.");
    } finally {
      setIsMessaging(false);
    }
  };

  return (
    <div className="show-card-modal-overlay" role="presentation">
      <div
        className="show-card-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="show-card-modal-title"
      >
        <div className="show-card-modal-header">
          <div className="show-card-modal-header-content">
            <h2 id="show-card-modal-title">{artistName}</h2>
            <p className="show-card-modal-venue">{venueName}</p>
          </div>
          <div className="show-card-modal-header-actions">
            <button
              type="button"
              className="show-card-modal-message"
              aria-label="Message artist"
              onClick={handleMessageArtist}
              disabled={isMessaging || !artistUserId}
              title="Message artist"
            >
              💬
            </button>
            <button
              type="button"
              className="show-card-modal-close"
              aria-label="Close show details modal"
              onClick={handleModalClose}
              disabled={isSaving}
            >
              ×
            </button>
          </div>
        </div>

        {loading ? (
          <div className="show-card-modal-body show-card-modal-body--loading">
            <div className="show-card-modal-loading show-card-modal-loading--centered">
              <span className="spinner" aria-hidden="true" />
              <span>Loading earnings…</span>
            </div>
          </div>
        ) : isEditing ? (
          <form className="show-card-modal-form" onSubmit={handleSave}>
            <div className="show-card-modal-body">
              <div className="show-card-modal-artist">
                <div className="show-card-modal-image-wrapper">
                  <img
                    src={tempArtistImage}
                    alt={`${artistName} profile`}
                    className="show-card-modal-image"
                  />
                </div>
                <div className="show-card-modal-info">
                  <div className="show-card-modal-info-item">
                    <label className="label" htmlFor="show-card-artist">
                      Artist
                    </label>
                    <select
                      id="show-card-artist"
                      className="show-card-modal-input"
                      value={formState.artistId}
                      onChange={handleInputChange("artistId")}
                      disabled={artistsLoading || isSaving}
                      required
                    >
                      <option value="" disabled>
                        {artistsLoading
                          ? "Loading artists..."
                          : "Select artist"}
                      </option>
                      {artists.map((artist) => (
                        <option key={artist.artist_id} value={artist.artist_id}>
                          {artist.stage_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="show-card-modal-info-item">
                    <label className="label" htmlFor="show-card-date">
                      Date
                    </label>
                    <input
                      id="show-card-date"
                      type="date"
                      className="show-card-modal-input"
                      value={formState.date}
                      onChange={handleInputChange("date")}
                      required
                      disabled={isSaving}
                    />
                  </div>
                  {namedSlotsAvailable && !useCustomTimes && (
                    <div className="show-card-modal-info-item">
                      <label className="label" htmlFor="show-card-time-slot">
                        Time slot
                      </label>
                      <select
                        id="show-card-time-slot"
                        className="show-card-modal-input"
                        value={formState.timeSlotId}
                        onChange={handleEditTimeSlotChange}
                        disabled={isSaving}
                      >
                        <option value="">Select a time slot</option>
                        {timeSlotOptions.map((slot) => (
                          <option key={slot.id} value={slot.id}>
                            {slot.name} ({slot.start_time?.slice(0, 5)} -{" "}
                            {slot.end_time?.slice(0, 5)})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {namedSlotsAvailable && (
                    <div className="add-show-custom-times-row">
                      <button
                        type="button"
                        className="add-show-custom-times-toggle"
                        onClick={handleToggleEditCustomTimes}
                        disabled={isSaving}
                        aria-expanded={useCustomTimes}
                      >
                        {useCustomTimes
                          ? "Use a venue time slot instead"
                          : "Use custom start and end times instead"}
                      </button>
                    </div>
                  )}
                  {!namedSlotsAvailable && (
                    <p className="add-show-helper">
                      No named time slots for this day — enter custom start and
                      end times.
                    </p>
                  )}
                  {(useCustomTimes || !namedSlotsAvailable) && (
                    <div className="show-card-modal-time-row">
                      <div className="show-card-modal-info-item">
                        <label className="label" htmlFor="show-card-start">
                          Start Time
                        </label>
                        <input
                          id="show-card-start"
                          type="time"
                          className="show-card-modal-input"
                          value={formState.startTime}
                          onChange={handleInputChange("startTime")}
                          required={useCustomTimes || !namedSlotsAvailable}
                          disabled={isSaving}
                        />
                      </div>
                      <div className="show-card-modal-info-item">
                        <label className="label" htmlFor="show-card-end">
                          End Time
                        </label>
                        <input
                          id="show-card-end"
                          type="time"
                          className="show-card-modal-input"
                          value={formState.endTime}
                          onChange={handleInputChange("endTime")}
                          required={useCustomTimes || !namedSlotsAvailable}
                          disabled={isSaving}
                        />
                      </div>
                    </div>
                  )}
                  {timeError && (useCustomTimes || !namedSlotsAvailable) && (
                    <div
                      className="show-card-modal-error"
                      style={{ marginTop: "0.5rem" }}
                    >
                      {timeError}
                    </div>
                  )}
                </div>
              </div>

              <div className="show-card-modal-statistics">
                <div className="show-card-modal-stat">
                  <label className="label" htmlFor="show-card-basepay">
                    Base Pay
                  </label>
                  <div className="show-card-modal-input-wrapper">
                    <span className="currency-prefix">$</span>
                    <input
                      id="show-card-basepay"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      className="show-card-modal-input"
                      value={formState.basePay}
                      onChange={handleNumberInput("basePay")}
                      disabled={isSaving}
                      placeholder="0"
                    />
                  </div>
                  <span className="value value--muted">
                    {formatCurrency(formState.basePay || 0)}
                  </span>
                </div>
                <div className="show-card-modal-stat">
                  <label className="label" htmlFor="show-card-sales">
                    Sales
                  </label>
                  <div className="show-card-modal-input-wrapper">
                    <span className="currency-prefix">$</span>
                    <input
                      id="show-card-sales"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      className="show-card-modal-input"
                      value={formState.sales}
                      onChange={handleNumberInput("sales")}
                      disabled={isSaving}
                      placeholder="0"
                    />
                  </div>
                  <span className="value value--muted">
                    {formatCurrency(formState.sales || 0)}
                  </span>
                </div>
              </div>

              {loading && (
                <div className="show-card-modal-loading">
                  <span className="spinner" aria-hidden="true" />
                  <span>Loading earnings…</span>
                </div>
              )}

              {error && (
                <div className="show-card-modal-error" role="alert">
                  {error}
                </div>
              )}
            </div>

            <div className="show-card-modal-footer">
              <button
                type="button"
                className="show-card-modal-secondary-btn"
                onClick={handleCancelEdit}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="show-card-modal-primary-btn"
                disabled={isSaving || loading || artistsLoading}
              >
                {isSaving ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </form>
        ) : (
          <>
            <div className="show-card-modal-body">
              <div className="show-card-modal-artist">
                <div className="show-card-modal-image-wrapper">
                  <img
                    src={tempArtistImage}
                    alt={`${artistName} profile`}
                    className="show-card-modal-image"
                  />
                </div>
                <div className="show-card-modal-info">
                  <div className="show-card-modal-info-item">
                    <span className="label">Artist</span>
                    <span className="value">{artistUserName || "—"}</span>
                  </div>
                  <div className="show-card-modal-info-item">
                    <span className="label">Date</span>
                    <span className="value">{dateDisplay || "—"}</span>
                  </div>
                  <div className="show-card-modal-info-item">
                    <span className="label">Time</span>
                    <span className="value">
                      {startTimeDisplay && endTimeDisplay
                        ? `${startTimeDisplay} - ${endTimeDisplay}`
                        : "—"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="show-card-modal-statistics">
                <div className="show-card-modal-stat">
                  <span className="label">Base Pay</span>
                  <span className="value">{basePayDisplay}</span>
                </div>
                <div className="show-card-modal-stat">
                  <span className="label">Sales</span>
                  <span className="value">{salesDisplay}</span>
                </div>
              </div>

              {loading && (
                <div className="show-card-modal-loading">
                  <span className="spinner" aria-hidden="true" />
                  <span>Loading earnings…</span>
                </div>
              )}

              {error && (
                <div className="show-card-modal-error" role="alert">
                  {error}
                </div>
              )}

              {saveMessage && !error && (
                <div className="show-card-modal-success" role="status">
                  {saveMessage}
                </div>
              )}
            </div>

            <div className="show-card-modal-footer">
              <button
                type="button"
                className="show-card-modal-secondary-btn"
                onClick={handleModalClose}
                disabled={isSaving}
              >
                Close
              </button>
              <button
                type="button"
                className="show-card-modal-danger-btn"
                onClick={confirmDelete}
                disabled={isSaving || isDeleting}
              >
                {isDeleting
                  ? isPastShow
                    ? "Removing…"
                    : "Deleting…"
                  : isPastShow
                    ? "Remove from my history"
                    : "Delete"}
              </button>
              <button
                type="button"
                className="show-card-modal-primary-btn"
                onClick={handleStartEdit}
                disabled={loading || artistsLoading || !detailSource}
              >
                Edit
              </button>
            </div>
          </>
        )}
      </div>
      <ConfirmModal
        open={isDeleteConfirmationOpen}
        title={isPastShow ? "Remove from my history" : "Delete Show"}
        message={
          isPastShow
            ? "This will only remove the show from your view and earnings. The artist will still see it. This cannot be undone."
            : "This will cancel the show for both you and the artist. They will be notified. This cannot be undone."
        }
        confirmLabel={isPastShow ? "Remove" : "Delete"}
        cancelLabel="Cancel"
        isConfirming={isDeleting}
        onConfirm={handleDelete}
        onCancel={() => {
          if (!isDeleting) {
            setIsDeleteConfirmationOpen(false);
          }
        }}
      />
    </div>
  );
};

export default ShowCardModal;
